/*
  Vollebak in-house sign-up engine (vb-capture).

  Replaces Klaviyo's onsite forms (popups, waiting-list popups, back-in-stock
  button) with theme-rendered ones that ad blockers leave alone, while still
  writing straight into Klaviyo through its public client API — the same
  pattern as the FWAHBL email capture in the Custom text block
  (ai_gen_block_0176200). Klaviyo's onsite JS is blocked by DNS / tracking
  lists (OISD blocks fast.a.klaviyo.com, where forms load from); the client
  subscription endpoints are not.

  Pieces (all markup is rendered by Liquid, this file only wires behaviour):
    form[data-vb-capture]          any sign-up form (list / back-in-stock)
    dialog[data-vb-popup="handle"] a popup; auto-opens per its data-* rules
    [data-vb-open="handle"]        opens a popup (also a[href="#vb-popup-handle"])
    vb-notify                      sold-out "join the waiting list" on PDPs

  Names deliberately avoid "klaviyo" / "newsletter" / "popup" class names:
  annoyance lists hide those cosmetically.

  QA: window.vbCapture.state() returns the current decision data.
*/
(function () {
  if (window.vbCapture) return;

  var CFG = window.vbCaptureConfig || {};
  var COMPANY = CFG.companyId || 'Ve7Y7L';
  var REVISION = '2024-10-15';
  var API = 'https://a.klaviyo.com/client/';
  var LS_KEY = 'vb_capture';
  var SS_KEY = 'vb_capture_session';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var DESIGN_MODE = !!CFG.designMode;

  /* ---------------- storage (never throws) ---------------- */
  function readStore(storage, key) {
    try { return JSON.parse(window[storage].getItem(key) || '{}') || {}; } catch (e) { return {}; }
  }
  function writeStore(storage, key, value) {
    try { window[storage].setItem(key, JSON.stringify(value)); } catch (e) {}
  }
  function local() { return readStore('localStorage', LS_KEY); }
  function session() { return readStore('sessionStorage', SS_KEY); }
  function patchLocal(fn) { var s = local(); fn(s); writeStore('localStorage', LS_KEY, s); }
  function patchSession(fn) { var s = session(); fn(s); writeStore('sessionStorage', SS_KEY, s); }

  /* ---------------- Klaviyo blocked? ---------------- */
  // A blocked request rejects with TypeError; an allowed no-cors HEAD resolves
  // (opaque). Probes the two hosts Klaviyo's popups depend on. Cached per session.
  var blockedPromise = null;
  function klaviyoBlocked() {
    // Checked fresh on every page (not remembered for the session): a visitor
    // can switch an ad blocker on or off between pages.
    if (blockedPromise) return blockedPromise;
    var probe = function (url) {
      return fetch(url, { method: 'HEAD', mode: 'no-cors', cache: 'no-store', credentials: 'omit' })
        .then(function () { return false; }, function () { return true; });
    };
    blockedPromise = Promise.all([
      probe('https://static.klaviyo.com/onsite/js/' + COMPANY + '/klaviyo.js?company_id=' + COMPANY),
      probe('https://fast.a.klaviyo.com/forms/api/v7/full-forms?company_id=' + COMPANY)
    ]).then(function (r) {
      return r[0] || r[1];
    });
    return blockedPromise;
  }

  // Klaviyo marks identified browsers in the __kla_id cookie (base64 JSON with
  // $exchange_id / $email) — the same signal its "existing user" rule uses.
  function knownSubscriber() {
    if (local().subscribed) return true;
    try {
      var m = document.cookie.match(/(?:^|;\s*)__kla_id=([^;]+)/);
      if (!m) return false;
      var data = JSON.parse(atob(decodeURIComponent(m[1])));
      return !!(data && (data.$exchange_id || data.$email));
    } catch (e) { return false; }
  }

  /* ---------------- Klaviyo client API ---------------- */
  function post(path, payload) {
    var url = API + path + '/?company_id=' + encodeURIComponent(COMPANY);
    var opts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REVISION },
      body: JSON.stringify(payload)
    };
    var direct = fetch(url, opts);
    return direct.then(checkResponse, function (err) {
      // Network-level failure = blocked (AdGuard blocks a.klaviyo.com except
      // client/subscriptions). Retry through the first-party relay if set.
      if (!CFG.relayUrl) throw err;
      return fetch(CFG.relayUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ path: path, payload: payload })
      }).then(checkResponse);
    });
  }
  function checkResponse(res) {
    if (res.ok) return res;
    return res.json().catch(function () { return {}; }).then(function (err) {
      var detail = err && err.errors && err.errors[0] ? err.errors[0].detail : 'HTTP ' + res.status;
      throw new Error(detail);
    });
  }
  function profile(email, properties) {
    var attributes = { email: email };
    if (properties && Object.keys(properties).length) attributes.properties = properties;
    return { data: { type: 'profile', attributes: attributes } };
  }
  function subscribeToList(listId, email, source, properties) {
    return post('subscriptions', {
      data: {
        type: 'subscription',
        attributes: { custom_source: source || 'Vollebak sign-up', profile: profile(email, properties) },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
  }
  function subscribeBackInStock(variantId, email) {
    return post('back-in-stock-subscriptions', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: { channels: ['EMAIL'], profile: profile(email) },
        relationships: { variant: { data: { type: 'catalog-variant', id: '$shopify:::$default:::' + variantId } } }
      }
    });
  }

  /* ---------------- form submit ---------------- */
  function submitForm(form) {
    if (form.hasAttribute('data-submitting') || form.getAttribute('data-state') === 'success') return;
    var input = form.querySelector('[name="vb-email"]');
    var email = (input && input.value || '').trim();
    var hp = form.querySelector('[name="vb-website"]');
    if (hp && hp.value) { succeed(form, email, true); return; } // bot: pretend
    if (!EMAIL_RE.test(email)) { fail(form, form.getAttribute('data-invalid-message'), input); return; }

    var properties = {};
    var missing = null;
    var tooLow = null;
    var bids = [];
    form.querySelectorAll('[data-property]').forEach(function (el) {
      var name = el.getAttribute('data-property');
      if (el.hasAttribute('data-bid')) {
        // Bid: clean values for Klaviyo — a real number, the currency code,
        // and the amount in the shop currency so bids compare across markets.
        var amount = parseInt((el.value || '').replace(/\D/g, ''), 10) || 0;
        if (!amount) { if (el.required && !missing) missing = el; return; }
        var min = parseFloat(el.getAttribute('data-min'));
        if (min && amount < min) { if (!tooLow) tooLow = el; return; }
        var currency = el.getAttribute('data-currency') || '';
        var base = el.getAttribute('data-base-currency') || '';
        properties[name + ' Amount'] = amount;
        if (currency) properties[name + ' Currency'] = currency;
        var rate = currency === base ? 1 : parseFloat(window.Shopify && window.Shopify.currency && window.Shopify.currency.active === currency && window.Shopify.currency.rate);
        var baseAmount = base && rate > 0 ? Math.round(amount / rate) : '';
        if (baseAmount !== '') properties[name + ' ' + base] = baseAmount;
        bids.push({ tab: name, email: email, amount: amount, currency: currency, baseCurrency: base, baseAmount: baseAmount,
          country: (window.Shopify && window.Shopify.country) || '', page: location.pathname });
        return;
      }
      var v = (el.value || '').trim();
      if (v) properties[name] = v;
      else if (el.required && !missing) missing = el;
    });
    if (missing) { missing.reportValidity(); return; }
    if (tooLow) { fail(form, tooLow.getAttribute('data-min-message'), tooLow); return; }

    var kind = form.getAttribute('data-kind') || 'list';
    var source = form.getAttribute('data-source') || '';
    var jobs = [];
    if (kind === 'bis') {
      var variant = form.querySelector('[name="vb-variant"]');
      var variantId = variant && variant.value;
      if (!variantId) { if (variant && variant.reportValidity) variant.reportValidity(); return; }
      jobs.push(subscribeBackInStock(variantId, email));
      var opt = form.querySelector('[name="vb-optin"]');
      if (opt && opt.checked && opt.getAttribute('data-list-id')) {
        jobs.push(subscribeToList(opt.getAttribute('data-list-id'), email, source));
      }
    } else {
      var listId = form.getAttribute('data-list-id');
      if (!listId) {
        console.error('[vb-capture] form "' + (form.getAttribute('data-handle') || '') + '" has no Klaviyo list ID');
        fail(form, form.getAttribute('data-error-message'));
        track('email_submit_error', form, 'missing list id');
        return;
      }
      jobs.push(subscribeToList(listId, email, source, properties));
    }

    form.setAttribute('data-submitting', '');
    Promise.all(jobs).then(function () {
      form.removeAttribute('data-submitting');
      succeed(form, email, false);
      bids.forEach(logBid);
    }, function (err) {
      form.removeAttribute('data-submitting');
      console.error('[vb-capture] subscription failed:', err && err.message);
      fail(form, form.getAttribute('data-error-message'));
      track('email_submit_error', form, err && err.message);
    });
  }

  // Bid log: one row per bid in the bids Google Sheet (Theme settings >
  // Vollebak sign-up > Bid log), sent once Klaviyo has accepted the bid.
  // Fire-and-forget: a failure here never reaches the customer.
  function logBid(bid) {
    if (!CFG.bidSheetUrl) return;
    try {
      fetch(CFG.bidSheetUrl, { method: 'POST', mode: 'no-cors', keepalive: true,
        headers: { 'Content-Type': 'text/plain;charset=utf-8' }, body: JSON.stringify(bid) }).catch(function () {});
    } catch (e) {}
  }

  function fail(form, message, focusEl) {
    var el = form.querySelector('.vb-capture__error');
    if (el) el.textContent = message || '';
    form.setAttribute('data-state', 'error');
    form.querySelectorAll('[aria-invalid]').forEach(function (i) { i.removeAttribute('aria-invalid'); });
    if (focusEl && focusEl.focus) { focusEl.setAttribute('aria-invalid', 'true'); focusEl.focus(); }
    fitDialog(form.closest('dialog'));
  }

  // Bid fields: digits only, grouped as typed (70000 -> 70,000), caret kept.
  document.addEventListener('input', function (e) {
    var el = e.target;
    if (!(el instanceof Element) || !el.hasAttribute('data-bid')) return;
    var caret = el.selectionStart == null ? el.value.length : el.selectionStart;
    var digitsBefore = el.value.slice(0, caret).replace(/\D/g, '').length;
    var digits = el.value.replace(/\D/g, '').replace(/^0+/, '').slice(0, 9);
    var out = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    el.value = out;
    var pos = 0, seen = 0;
    while (pos < out.length && seen < digitsBefore) { if (/\d/.test(out.charAt(pos))) seen++; pos++; }
    try { el.setSelectionRange(pos, pos); } catch (err) {}
    if (el.hasAttribute('aria-invalid')) { // the "below minimum" message goes as soon as they correct it
      el.removeAttribute('aria-invalid');
      var msg = el.form && el.form.querySelector('.vb-capture__error');
      if (msg) msg.textContent = '';
    }
  });

  function succeed(form, email, bot) {
    form.setAttribute('data-state', 'success');
    var err = form.querySelector('.vb-capture__error');
    if (err) err.textContent = '';
    fitDialog(form.closest('dialog'));
    var status = form.querySelector('[role="status"]');
    var success = form.querySelector('.vb-capture__success');
    if (status && success) status.textContent = success.textContent.trim();
    if (bot) return;
    var handle = form.getAttribute('data-handle');
    patchLocal(function (s) {
      if (form.getAttribute('data-kind') !== 'bis') s.subscribed = true;
      if (handle) { s.done = s.done || {}; s.done[handle] = Date.now(); }
    });
    // Let Klaviyo's own JS (when it is running) know who this is, so its
    // forms treat them as an existing user and attribution keeps working.
    try { if (window.klaviyo && window.klaviyo.identify) window.klaviyo.identify({ email: email }); } catch (e) {}
    track('email_submit', form);
    form.dispatchEvent(new CustomEvent('vb-capture:success', { bubbles: true }));
  }

  function track(event, form, extra) {
    if (window.vbEngagement && window.vbEngagement.track) {
      window.vbEngagement.track(event, form.getAttribute('data-engagement-cta') || 'vb_capture', extra || form.getAttribute('data-source') || '');
    }
  }

  document.addEventListener('submit', function (e) {
    var form = e.target instanceof Element ? e.target.closest('form[data-vb-capture]') : null;
    if (!form) return;
    e.preventDefault();
    submitForm(form);
  });
  document.addEventListener('input', function (e) {
    var form = e.target instanceof Element ? e.target.closest('form[data-vb-capture]') : null;
    if (form && form.getAttribute('data-state') === 'error') form.removeAttribute('data-state');
  });

  /* ---------------- dialogs ---------------- */
  function popupFor(handle) {
    return handle ? document.querySelector('dialog[data-vb-popup="' + CSS.escape(handle) + '"]') : null;
  }

  function openPopup(dialog, how) {
    if (!dialog || dialog.open) return;
    try { dialog.showModal(); } catch (e) { dialog.setAttribute('open', ''); }
    afterOpen(dialog, how);
  }

  // Everything that follows a popup appearing. Kept apart from showModal() so
  // a popup the page opened before this file arrived (the tiny inline scripts
  // in theme.liquid / vb-popups.liquid, marked data-vb-early) is adopted
  // rather than opened twice.
  function afterOpen(dialog, how) {
    if (dialog.__vbOpened) return;
    dialog.__vbOpened = true;
    dialog.removeAttribute('data-vb-early');
    var handle = dialog.getAttribute('data-vb-popup');
    var chip = chipFor(handle);
    if (chip) chip.hidden = true;
    dialog.setAttribute('data-opened-by', how || 'manual');
    document.documentElement.classList.add('vb-capture-open');
    patchSession(function (s) {
      s.shown = s.shown || {};
      s.shown[handle] = Date.now();
      if (s.min) delete s.min[handle];
    });
    fitDialog(dialog);
    // Focus the popup itself, not the email field: no field starts out
    // selected (Adam 19/10), and no mobile keyboard pops up uninvited.
    dialog.focus({ preventScroll: true });
  }

  // A window shorter than the popup (e.g. a laptop browser with a tall
  // toolbar) used to make the popup scroll. Instead the whole popup is scaled
  // down to fit, design intact, like an image. Bottom sheets and full-screen
  // mobile popups are left alone, and below 55% it scrolls rather than shrink
  // the text further.
  function fitDialog(dialog) {
    if (!dialog || !dialog.open || dialog.classList.contains('is-editor-preview')) return;
    dialog.removeAttribute('data-vb-fit');
    dialog.removeAttribute('data-vb-scroll');
    dialog.style.removeProperty('--vbc-fit');
    var natural = dialog.scrollHeight;
    var overflows = natural > dialog.clientHeight + 1;
    if (window.matchMedia('(max-width: 749px)').matches &&
        (dialog.classList.contains('vb-capture-dialog--m-sheet') || dialog.classList.contains('vb-capture-dialog--m-full'))) {
      if (overflows) dialog.setAttribute('data-vb-scroll', '');
      return;
    }
    var avail = window.innerHeight - 32;
    if (natural <= avail + 1) { if (overflows) dialog.setAttribute('data-vb-scroll', ''); return; }
    var z = avail / natural;
    if (z < 0.55) { dialog.setAttribute('data-vb-scroll', ''); return; } // too small to shrink: scroll instead
    dialog.style.setProperty('--vbc-fit', z.toFixed(4));
    dialog.setAttribute('data-vb-fit', '');
  }
  // web fonts can change a popup's height after it opens
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () {
    document.querySelectorAll('dialog[data-vb-popup][open]').forEach(fitDialog);
  });
  var fitQueued = false;
  window.addEventListener('resize', function () {
    if (fitQueued) return;
    fitQueued = true;
    requestAnimationFrame(function () {
      fitQueued = false;
      document.querySelectorAll('dialog[data-vb-popup][open]').forEach(fitDialog);
    });
  });

  function closePopup(dialog) {
    if (!dialog || !dialog.open) return;
    dialog.close();
  }

  function onClosed(dialog) {
    dialog.__vbOpened = false;
    document.documentElement.classList.toggle('vb-capture-open', !!document.querySelector('dialog[data-vb-popup][open]'));
    var handle = dialog.getAttribute('data-vb-popup');
    var form = dialog.querySelector('form[data-vb-capture]');
    var done = form && form.getAttribute('data-state') === 'success';
    if (!done) {
      patchLocal(function (s) { s.dismissed = s.dismissed || {}; s.dismissed[handle] = Date.now(); });
      if (dialog.hasAttribute('data-chip')) {
        patchSession(function (s) { s.min = s.min || {}; s.min[handle] = true; });
        showChip(handle);
      }
    }
  }

  function chipFor(handle) {
    return handle ? document.querySelector('[data-vb-chip="' + CSS.escape(handle) + '"]') : null;
  }
  function showChip(handle) {
    var chip = chipFor(handle);
    if (chip) chip.hidden = false;
  }

  document.addEventListener('click', function (e) {
    if (!(e.target instanceof Element)) return;
    // open triggers
    var opener = e.target.closest('[data-vb-open], a[href^="#vb-popup-"]');
    if (opener) {
      var handle = opener.getAttribute('data-vb-open') || opener.getAttribute('href').slice('#vb-popup-'.length);
      var dialog = popupFor(handle);
      e.preventDefault();
      if (dialog) { routeManual(opener, dialog); return; }
      // Button points at a popup that isn't on this page (switched off, or
      // its page rules exclude it): fall back to the Klaviyo form if given.
      var kForm = opener.getAttribute('data-klaviyo-form-desktop') || opener.getAttribute('data-klaviyo-form-mobile');
      if (kForm && window._klOnsite && window._klOnsite.push) window._klOnsite.push(['openForm', kForm]);
      else console.warn('[vb-capture] no popup with handle "' + handle + '" on this page');
      return;
    }
    // close button
    var closer = e.target.closest('[data-vb-close]');
    if (closer) {
      var cd = closer.closest('dialog');
      if (cd && cd.classList.contains('is-editor-preview')) previewClose(cd); else closePopup(cd);
      return;
    }
    // backdrop click (the dialog element itself is the backdrop area)
    if (e.target.matches('dialog[data-vb-popup]')) closePopup(e.target);
  });

  // Manual openers can defer to Klaviyo's own form while Klaviyo still runs
  // (data-show="blocked" + data-klaviyo-form-*) — same button, either route.
  function routeManual(opener, dialog) {
    var show = opener.getAttribute('data-show') || 'always';
    var kForm = window.matchMedia('(max-width: 749px)').matches
      ? opener.getAttribute('data-klaviyo-form-mobile') || opener.getAttribute('data-klaviyo-form-desktop')
      : opener.getAttribute('data-klaviyo-form-desktop') || opener.getAttribute('data-klaviyo-form-mobile');
    if (show !== 'blocked' || !kForm || DESIGN_MODE) { openPopup(dialog, 'manual'); return; }
    klaviyoBlocked().then(function (blocked) {
      if (blocked || !window._klOnsite || !window._klOnsite.push) { openPopup(dialog, 'manual'); return; }
      window._klOnsite.push(['openForm', kForm]);
    });
  }

  function wireDialog(dialog) {
    if (dialog.__vbWired) return;
    dialog.__vbWired = true;
    dialog.addEventListener('close', function () { onClosed(dialog); });
  }

  /* ---------------- auto-open rules ---------------- */
  function globToRegExp(glob) {
    var esc = glob.trim().replace(/[.+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
    return new RegExp('^' + esc + '$', 'i');
  }
  function urlMatches(list) {
    if (!list) return false;
    var href = location.href;
    var path = location.pathname;
    return list.split(/[\n,]/).some(function (p) {
      p = p.trim();
      if (!p) return false;
      if (p.indexOf('*') === -1) return path === p || href === p;
      var re = globToRegExp(p);
      return re.test(href) || re.test(path);
    });
  }

  // Returns null when the popup may auto-open, else the reason it may not.
  function blockReason(dialog) {
    var d = dialog.dataset;
    var handle = d.vbPopup;
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    if (d.trigger === 'manual') return 'manual-only';
    if (d.device === 'desktop' && mobile) return 'device';
    if (d.device === 'mobile' && !mobile) return 'device';
    if (d.include && !urlMatches(d.include)) return 'not-included';
    if (urlMatches(d.exclude)) return 'excluded';
    var st = local();
    if (st.done && st.done[handle]) return 'signed-up';
    if (d.hideKnown === 'true' && knownSubscriber()) return 'known-subscriber';
    // Klaviyo's "show again N days after closing"; 0 = once per session.
    var days = parseFloat(d.days || '0');
    var dismissed = st.dismissed && st.dismissed[handle];
    if (days > 0 && dismissed && Date.now() - dismissed < days * 864e5) return 'dismissed';
    if ((session().shown || {})[handle]) return 'shown-this-session';
    return null;
  }

  var autoOpened = false;
  function anotherOverlayOpen() {
    return !!document.querySelector('dialog[open]:not([data-vb-popup]), .drawer[data-open], #Details-menu-drawer-container[open]') ||
      document.documentElement.classList.contains('vb-capture-open');
  }

  function armAuto(dialog) {
    var d = dialog.dataset;
    var handle = d.vbPopup;
    if ((session().min || {})[handle] && dialog.hasAttribute('data-chip')) { showChip(handle); return; }
    var reason = blockReason(dialog);
    if (reason) { dialog.setAttribute('data-vb-skip', reason); return; }
    var gate = d.show === 'blocked' && !DESIGN_MODE ? klaviyoBlocked() : Promise.resolve(true);
    gate.then(function (ok) {
      if (!ok) { dialog.setAttribute('data-vb-skip', 'klaviyo-running'); return; }
      var fire = function () {
        if (autoOpened) return;
        // wait for other overlays (cart drawer, menu, search) to close
        var tries = 0;
        (function attempt() {
          if (autoOpened) return;
          if (anotherOverlayOpen() && tries++ < 120) { setTimeout(attempt, 1000); return; }
          autoOpened = true;
          openPopup(dialog, 'auto');
        })();
      };
      if (d.trigger === 'scroll') {
        var pct = parseFloat(d.scroll || '50') / 100;
        var onScroll = function () {
          var el = document.scrollingElement || document.documentElement;
          var max = el.scrollHeight - window.innerHeight;
          if (max <= 0 || el.scrollTop / max >= pct) {
            window.removeEventListener('scroll', onScroll);
            fire();
          }
        };
        window.addEventListener('scroll', onScroll, { passive: true });
      } else if (d.trigger === 'exit') {
        var onLeave = function (e) {
          if (e.clientY > 0 || e.relatedTarget) return;
          document.removeEventListener('mouseout', onLeave);
          fire();
        };
        document.addEventListener('mouseout', onLeave);
        // touch screens have no exit intent: fall back to the delay
        if (!window.matchMedia('(hover: hover)').matches) setTimeout(fire, parseFloat(d.delay || '30') * 1000);
      } else {
        setTimeout(fire, parseFloat(d.delay || '0') * 1000);
      }
    });
  }

  /* ---------------- portal ---------------- */
  // Dialogs that must live inside the product <form> (a nested <form> is
  // dropped by the HTML parser) are rendered in a <template> — parsed intact —
  // and moved to <body> here. A re-render replaces the previous copy.
  if (!customElements.get('vb-portal')) {
    customElements.define('vb-portal', class extends HTMLElement {
      connectedCallback() { this.tryMove(); }
      // Engine loads async from <head>, so this element can connect before its
      // <template> has been parsed. It is complete once the parser has moved
      // past it (something follows it) or the document is done; early() below
      // retries while the page is still streaming in.
      tryMove() {
        if (this.__moved) return;
        var tpl = this.querySelector('template');
        if (!tpl) return;
        if (document.readyState === 'loading' && !this.nextSibling && !(this.parentNode && this.parentNode.nextSibling)) return;
        this.__moved = true;
        var frag = tpl.content.cloneNode(true);
        Array.prototype.forEach.call(frag.querySelectorAll('[data-vb-popup], [data-vb-chip]'), function (el) {
          var key = el.hasAttribute('data-vb-popup') ? 'data-vb-popup' : 'data-vb-chip';
          var old = document.body.querySelector(':scope > [' + key + '="' + CSS.escape(el.getAttribute(key)) + '"]');
          if (old && old !== el) old.remove();
        });
        var dialogs = Array.prototype.slice.call(frag.querySelectorAll('dialog[data-vb-popup]'));
        document.body.appendChild(frag);
        dialogs.forEach(wireDialog);
      }
    });
  }

  /* ---------------- sold-out waiting list (PDP) ---------------- */
  // <vb-notify> sits beside the add-to-cart button. When the selected variant
  // is sold out it replaces the theme's disabled "Sold out" button (exactly
  // where Klaviyo's back-in-stock button sat) and opens its own dialog.
  if (!customElements.get('vb-notify')) {
    customElements.define('vb-notify', class extends HTMLElement {
      connectedCallback() {
        if (this.__ready) return;
        // children (variant JSON) may not be parsed yet when the engine is early
        var json = this.querySelector('script[type="application/json"]');
        if (document.readyState === 'loading' && !(json && json.nextSibling)) return; // early() retries
        this.__ready = true;
        try { this.variants = JSON.parse(this.querySelector('script[type="application/json"]').textContent); }
        catch (e) { this.variants = []; }
        this.host = this.closest('[data-vb-notify-host]') || this.parentElement;
        this.onUpdate = this.onUpdate.bind(this);
        document.addEventListener('variant:update', this.onUpdate);
        document.addEventListener('change', this.onUpdate);
        this.decide();
      }
      disconnectedCallback() {
        document.removeEventListener('variant:update', this.onUpdate);
        document.removeEventListener('change', this.onUpdate);
        this.__ready = false;
      }
      onUpdate(e) {
        // Changes inside a popup (its own size picker, the email field) are not
        // the page's size changing: reacting to them snapped the popup's size
        // picker back to the page's size.
        if (e && e.target instanceof Element && e.target.closest('dialog[data-vb-popup]')) return;
        var id = e && e.detail && e.detail.resource && e.detail.resource.id;
        // Size radios (ai_gen_block_7ed16de) carry data-variant-id directly.
        if (!id && e && e.target instanceof Element && e.target.matches('[data-variant-id]')) id = e.target.getAttribute('data-variant-id');
        if (id) this.current = String(id);
        // let the theme finish swapping the button state first
        requestAnimationFrame(() => this.decide());
      }
      selectedVariant() {
        var form = this.closest('form') || document.querySelector('form[data-type="add-to-cart-form"]');
        var input = form && form.querySelector('input[name="id"]');
        var id = this.current || (input && input.value) || this.getAttribute('data-initial');
        return this.variants.find(function (v) { return String(v.id) === String(id); }) || null;
      }
      decide() {
        var v = this.selectedVariant();
        var allOut = this.variants.length > 0 && this.variants.every(function (x) { return !x.available; });
        var soldOut = v ? !v.available : allOut;
        var mode = this.getAttribute('data-show');
        var apply = (on) => {
          this.hidden = !on;
          if (this.host) this.host.toggleAttribute('data-vb-notify-active', on);
          var dialog = popupFor(this.getAttribute('data-handle'));
          var select = dialog && dialog.querySelector('select[name="vb-variant"]');
          if (on && select && v && !v.available) select.value = String(v.id);
        };
        var token = this.__decideToken = (this.__decideToken || 0) + 1; // a newer size choice cancels any pending check
        if (!soldOut || mode === 'off') { apply(false); return; }
        if (mode === 'blocked' && !DESIGN_MODE) {
          // Ours shows when Klaviyo is blocked, and also whenever Klaviyo's own
          // Back in Stock button hasn't appeared shortly after a sold-out size is
          // chosen (blockers that stop the button but not the hosts we probe,
          // cosmetic filters, a slow or failed Klaviyo load).
          var started = Date.now();
          var klaviyoButton = function () {
            var b = document.querySelector('#klaviyo-bis-button-container button, #klaviyo-bis-button-container a');
            return !!(b && b.getBoundingClientRect().height > 0 && getComputedStyle(b).visibility !== 'hidden');
          };
          var self = this;
          klaviyoBlocked().then(function (blocked) {
            if (token !== self.__decideToken) return;
            if (blocked) { apply(true); return; }
            (function watch() {
              if (token !== self.__decideToken) return;
              if (klaviyoButton()) { apply(false); return; }
              if (Date.now() - started > 2500) { apply(true); return; }
              setTimeout(watch, 250);
            })();
          });
        } else {
          apply(true);
        }
      }
    });
  }

  /* ---------------- artwork preload ----------------
     Popup images are loading="lazy", so they used to start downloading only
     when the popup opened (up to ~1.2s of empty card on 4G). Once the page has
     loaded we fetch the artwork for this viewport of every popup that can
     open here — armed auto popups and popups with an opener on the page —
     and immediately on hover / touch / focus of an opener. Popups that can't
     open on this page download nothing. */
  var preloaded = new WeakSet();
  function preloadDialog(dialog, priority) {
    if (!dialog || preloaded.has(dialog)) return;
    preloaded.add(dialog);
    var mobile = window.matchMedia('(max-width: 749px)').matches;
    dialog.querySelectorAll('.vb-capture-dialog__media--' + (mobile ? 'm' : 'd') + ' img').forEach(function (img) {
      img.fetchPriority = priority || 'auto';
      img.loading = 'eager';
      if (img.decode) img.decode().catch(function () {});
    });
    // Web fonts download on first use, so a campaign face (Suisse Cond,
    // Classic Comic…) used to swap in after the popup opened. Load the
    // heading and button faces now.
    if (document.fonts && document.fonts.load) {
      dialog.querySelectorAll('.vb-capture__heading, .vb-capture__submit, .vb-capture__input').forEach(function (el) {
        var cs = getComputedStyle(el);
        document.fonts.load(cs.fontWeight + ' 16px ' + cs.fontFamily).catch(function () {});
      });
    }
  }
  function preloadCandidates() {
    document.querySelectorAll('dialog[data-vb-popup]').forEach(function (d) {
      var h = d.getAttribute('data-vb-popup');
      var hasOpener = !!document.querySelector('[data-vb-open="' + CSS.escape(h) + '"], a[href="#vb-popup-' + CSS.escape(h) + '"]');
      var armed = d.hasAttribute('data-auto') && !d.hasAttribute('data-vb-skip');
      if (hasOpener) preloadDialog(d, 'auto');
      else if (armed) preloadDialog(d, 'low');
    });
  }
  function onIntent(e) {
    var o = e.target instanceof Element ? e.target.closest('[data-vb-open], a[href^="#vb-popup-"]') : null;
    if (!o) return;
    var h = o.getAttribute('data-vb-open') || o.getAttribute('href').slice('#vb-popup-'.length);
    preloadDialog(popupFor(h), 'high');
  }
  ['pointerover', 'touchstart', 'focusin'].forEach(function (t) {
    document.addEventListener(t, onIntent, { passive: true, capture: true });
  });
  // Not window 'load': on pages with films that can take many seconds. Start
  // once the document is parsed and the main thread is free (low priority).
  function whenIdle(fn) {
    (window.requestIdleCallback || function (f) { return setTimeout(f, 300); })(fn, { timeout: 1000 });
  }

  /* ---------------- early: while the page is still arriving ----------------
     DOMContentLoaded on these pages is 1-3s after the first paint (the theme's
     own modules run first). Waiting for it made linked popups, early clicks
     and artwork all late. early() runs as soon as this file does and again as
     markup streams in, and does only what is already possible. */
  var deepLinkDone = false;
  function early() {
    document.querySelectorAll('vb-portal').forEach(function (el) { if (el.tryMove) el.tryMove(); });
    document.querySelectorAll('vb-notify').forEach(function (el) { if (el.connectedCallback && !el.__ready) el.connectedCallback(); });
    document.querySelectorAll('dialog[data-vb-popup]').forEach(function (d) {
      wireDialog(d);
      if (d.open && d.hasAttribute('data-vb-early')) afterOpen(d, 'manual'); // opened by the inline script
    });
    if (DESIGN_MODE) return;
    // artwork + fonts for popups that have a button on this page
    document.querySelectorAll('[data-vb-open]').forEach(function (o) {
      var d = popupFor(o.getAttribute('data-vb-open'));
      if (d) preloadDialog(d, 'auto');
    });
    // deep link: /any-page#vb-popup-handle
    if (!deepLinkDone && location.hash.indexOf('#vb-popup-') === 0) {
      var target = popupFor(decodeURIComponent(location.hash.slice(10)));
      if (target) { deepLinkDone = true; preloadDialog(target, 'high'); openPopup(target, 'manual'); afterOpen(target, 'manual'); }
    }
    // a tap that arrived before this file: replay it the moment its popup exists
    // (only once window.vbCapture exists, or the inline handler would queue the replay again)
    var queued = window.vbCapture ? window.vbCaptureQueue || [] : [];
    if (queued.length) {
      var last = queued[queued.length - 1];
      if (last && last.isConnected && popupFor(last.getAttribute('data-vb-open'))) { window.vbCaptureQueue = []; last.click(); }
    }
  }
  early();
  if (document.readyState === 'loading' && window.MutationObserver) {
    var earlyQueued = false;
    var earlyObserver = new MutationObserver(function () {
      if (earlyQueued) return;
      earlyQueued = true;
      setTimeout(function () { earlyQueued = false; early(); }, 0);
    });
    earlyObserver.observe(document.documentElement, { childList: true, subtree: true });
    document.addEventListener('DOMContentLoaded', function () { earlyObserver.disconnect(); }, { once: true });
  }

  /* ---------------- boot ---------------- */
  function boot() {
    early();
    // Klaviyo-blocked check up front, so a "blocked" mode button never waits on it
    if (!DESIGN_MODE && document.querySelector('[data-show="blocked"]')) klaviyoBlocked();
    if (DESIGN_MODE) { // editor: "Keep open while editing" + block selection only
      document.querySelectorAll('dialog[data-vb-popup][data-preview]').forEach(previewOpen);
      return;
    }
    document.querySelectorAll('dialog[data-vb-popup][data-auto]').forEach(armAuto);
    whenIdle(preloadCandidates);
    // a tap whose popup never turned up (e.g. switched off): let the normal handler deal with it
    var queued = window.vbCaptureQueue || [];
    window.vbCaptureQueue = [];
    var last = queued[queued.length - 1];
    if (last && last.isConnected) last.click();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor. Each pause while typing in a setting re-renders the section
  // and we re-show the selected popup. showModal() runs the browser's dialog
  // focusing steps, which pulls focus from the editor sidebar into the preview
  // frame — the cursor jumped out of the field being typed in. So in the
  // editor a preview is shown as a plain open dialog: no top layer, no focus.
  function previewOpen(dialog) {
    if (!dialog) return;
    dialog.classList.add('is-editor-preview');
    if (!dialog.open) dialog.setAttribute('open', '');
  }
  function previewClose(dialog) {
    if (!dialog) return;
    dialog.classList.remove('is-editor-preview');
    dialog.removeAttribute('open');
  }
  function dialogForEditorTarget(target) {
    if (!(target instanceof Element)) return null;
    if (target.matches('dialog[data-vb-popup]')) return target;
    var opener = target.querySelector('[data-vb-open]');
    return opener ? popupFor(opener.getAttribute('data-vb-open')) : null;
  }
  var selectedTarget = null;
  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('dialog[data-vb-popup]').forEach(wireDialog);
    document.querySelectorAll('dialog[data-vb-popup][data-preview]').forEach(previewOpen);
    // the re-rendered block is a new element: find it again by its block id
    if (selectedTarget && selectedTarget.dataset && selectedTarget.dataset.shopifyEditorBlock) {
      var again = document.querySelector('[data-shopify-editor-block="' + CSS.escape(selectedTarget.dataset.shopifyEditorBlock) + '"]');
      if (again) previewOpen(dialogForEditorTarget(again));
    }
  });
  document.addEventListener('shopify:block:select', function (e) {
    var d = dialogForEditorTarget(e.target);
    if (d) { selectedTarget = e.target; previewOpen(d); }
  });
  document.addEventListener('shopify:block:deselect', function (e) {
    var d = dialogForEditorTarget(e.target);
    if (d) { selectedTarget = null; if (!d.hasAttribute('data-preview')) previewClose(d); }
  });

  window.vbCapture = {
    open: function (handle) { openPopup(popupFor(handle), 'manual'); },
    close: function (handle) { closePopup(popupFor(handle)); },
    klaviyoBlocked: klaviyoBlocked,
    knownSubscriber: knownSubscriber,
    state: function () {
      return {
        local: local(),
        session: session(),
        popups: Array.prototype.map.call(document.querySelectorAll('dialog[data-vb-popup]'), function (d) {
          return { handle: d.dataset.vbPopup, open: d.open, skip: d.getAttribute('data-vb-skip'), reason: blockReason(d) };
  early(); // replays a queued tap now that the inline handler stands down
        })
      };
    },
    reset: function () { writeStore('localStorage', LS_KEY, {}); writeStore('sessionStorage', SS_KEY, {}); }
  };
})();
